import { useMemo, useState, type ReactElement } from 'react';
import { X } from 'lucide-react';
import { z } from 'zod';
import {
    CloudLoggingQueryRequestSchema,
    compileCloudLoggingFilter,
    stringifyLosslessJson,
    type CloudLoggingEntry,
    type CloudLoggingOption,
    type CloudLoggingQueryRequest,
    type LogSeverity,
} from '@anchorbase/pillar-sdk';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { Button } from '@/components/ui/button';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import type {
    InfiniteScrollListFetchPage,
    InfiniteScrollListRemoteFieldPredicateDescriptor,
} from '@/components/ui/infinite-scroll-list.types';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RemotePredicateMultiselect } from '@/components/ui/remote-predicate-multiselect';
import { SectionErrorBoundary } from '@/components/ui/section-error-boundary';
import {
    CloudLoggingOptionPageSize,
    fetchCloudLoggingLogIdOptionsPage,
    fetchCloudLoggingResourceTypeOptionsPage,
} from '@/lib/cloud-logging-option-fetchers';
import { loggingApi } from '@/lib/pillarApi';
import {
    buildActiveCloudLoggingQuery,
    createDefaultCloudLoggingQueryState,
    formatCloudLoggingTimestampInput,
    parseCloudLoggingTimestampInput,
    switchCloudLoggingQueryMode,
    updateGuidedCloudLoggingDraft,
    updateRawCloudLoggingDraft,
    type CloudLoggingQueryState,
    type GuidedCloudLoggingDraft,
} from './cloud-logging-query-state';

const CloudLoggingPageSize: number = 100;
const GuidedDisabledReason: string = 'Switch to Guided mode to edit this field.';
const RawDisabledReason: string = 'Switch to Raw mode to edit the filter directly.';
const CloudLoggingSeverities: ReadonlyArray<LogSeverity> = [
    'DEFAULT',
    'DEBUG',
    'INFO',
    'NOTICE',
    'WARNING',
    'ERROR',
    'CRITICAL',
    'ALERT',
    'EMERGENCY',
];

export const CloudLoggingFrontendCursorSchema: z.ZodString = z.string().min(1);

const ResourceTypeDescriptor: InfiniteScrollListRemoteFieldPredicateDescriptor = {
    field: 'resource_type',
    label: 'Resource Type',
    operator: 'in',
    valueType: 'string',
    remoteOptions: {
        pageSize: CloudLoggingOptionPageSize,
        fetchPage: fetchCloudLoggingResourceTypeOptionsPage,
    },
};

const LogIdDescriptor: InfiniteScrollListRemoteFieldPredicateDescriptor = {
    field: 'log_id',
    label: 'Log Name',
    operator: 'in',
    valueType: 'string',
    remoteOptions: {
        pageSize: CloudLoggingOptionPageSize,
        fetchPage: fetchCloudLoggingLogIdOptionsPage,
    },
};

interface CloudLoggingPageState {
    readonly draft: CloudLoggingQueryState;
    readonly executedQuery: CloudLoggingQueryRequest;
}

interface CloudLoggingModeSliderProps {
    readonly value: CloudLoggingQueryRequest['mode'];
    readonly onChange: (mode: CloudLoggingQueryRequest['mode']) => void;
}

interface CloudLoggingOptionSelectorProps {
    readonly descriptor: InfiniteScrollListRemoteFieldPredicateDescriptor;
    readonly selectedOptions: ReadonlyArray<CloudLoggingOption>;
    readonly disabled: boolean;
    readonly onChange: (options: ReadonlyArray<CloudLoggingOption>) => void;
}

interface CloudLoggingSeveritySelectorProps {
    readonly selectedValues: ReadonlyArray<LogSeverity>;
    readonly disabled: boolean;
    readonly onChange: (values: ReadonlyArray<LogSeverity>) => void;
}

interface CloudLoggingQueryControlsProps {
    readonly state: CloudLoggingQueryState;
    readonly onChange: (state: CloudLoggingQueryState) => void;
    readonly onSearch: () => void;
    readonly errorMessage: string | null;
}

function createInitialCloudLoggingPageState(): CloudLoggingPageState {
    const draft: CloudLoggingQueryState = createDefaultCloudLoggingQueryState(new Date());
    return { draft, executedQuery: buildActiveCloudLoggingQuery(draft) };
}

function parseCloudLoggingCursor(pageParam: unknown): string | undefined {
    if (pageParam === null || pageParam === undefined) {
        return undefined;
    }
    const result = CloudLoggingFrontendCursorSchema.safeParse(pageParam);
    if (!result.success) {
        throw new Error('Expected an opaque Cloud Logging cursor');
    }
    return result.data;
}

function CloudLoggingModeSlider({ value, onChange }: CloudLoggingModeSliderProps): ReactElement {
    return (
        <div
            role="radiogroup"
            aria-label="Cloud Logging query mode"
            className="inline-flex rounded-full border bg-white p-1"
        >
            {(['guided', 'raw'] as const).map((mode) => (
                <label
                    key={mode}
                    className={`cursor-pointer rounded-full px-4 py-2 text-sm font-medium ${
                        value === mode ? 'bg-primary text-primary-foreground' : 'text-gray-700'
                    }`}
                >
                    <input
                        type="radio"
                        name="cloud-logging-query-mode"
                        value={mode}
                        checked={value === mode}
                        className="sr-only"
                        onChange={() => onChange(mode)}
                    />
                    {mode === 'guided' ? 'Guided' : 'Raw'}
                </label>
            ))}
        </div>
    );
}

function CloudLoggingOptionSelector({
    descriptor,
    selectedOptions,
    disabled,
    onChange,
}: CloudLoggingOptionSelectorProps): ReactElement {
    const setSelected = (option: CloudLoggingOption, selected: boolean): void => {
        const nextOptions: ReadonlyArray<CloudLoggingOption> = selected
            ? [
                  ...selectedOptions.filter(
                      (currentOption) => currentOption.value !== option.value
                  ),
                  option,
              ]
            : selectedOptions.filter((currentOption) => currentOption.value !== option.value);
        onChange(nextOptions);
    };

    return (
        <div className="space-y-2">
            <Label>{descriptor.label}</Label>
            <RemotePredicateMultiselect
                descriptor={descriptor}
                selectedValues={selectedOptions.map((option) => option.value)}
                disabled={disabled}
                disabledReason={GuidedDisabledReason}
                onSelectionChange={setSelected}
            />
            {selectedOptions.length > 0 ? (
                <div className="flex flex-wrap gap-2" aria-label={`Selected ${descriptor.label}`}>
                    {selectedOptions.map((option) => (
                        <span
                            key={option.value}
                            className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-900"
                        >
                            {option.label}
                            <button
                                type="button"
                                aria-label={`Remove ${descriptor.label}: ${option.label}`}
                                disabled={disabled}
                                title={disabled ? GuidedDisabledReason : undefined}
                                className="inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                                onClick={() => setSelected(option, false)}
                            >
                                <X className="h-3 w-3" aria-hidden="true" />
                            </button>
                        </span>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function CloudLoggingSeveritySelector({
    selectedValues,
    disabled,
    onChange,
}: CloudLoggingSeveritySelectorProps): ReactElement {
    const setSelected = (severity: LogSeverity, selected: boolean): void => {
        onChange(
            selected
                ? [...new Set([...selectedValues, severity])]
                : selectedValues.filter((currentSeverity) => currentSeverity !== severity)
        );
    };

    return (
        <div className="space-y-2" title={disabled ? GuidedDisabledReason : undefined}>
            <Label>Severity</Label>
            <details
                className="relative"
                onToggle={(event) => {
                    if (disabled && event.currentTarget.open) {
                        event.currentTarget.open = false;
                    }
                }}
            >
                <summary
                    aria-label="Severity values"
                    aria-disabled={disabled}
                    className="flex h-9 min-w-[220px] cursor-pointer list-none items-center rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 aria-disabled:pointer-events-none aria-disabled:cursor-not-allowed aria-disabled:opacity-50"
                    onClick={(event) => {
                        if (disabled) {
                            event.preventDefault();
                        }
                    }}
                >
                    Select Severity
                </summary>
                <div className="absolute z-20 mt-1 max-h-72 min-w-[240px] overflow-y-auto rounded-md border bg-background p-2 shadow-md">
                    {CloudLoggingSeverities.map((severity) => (
                        <label
                            key={severity}
                            className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 text-sm hover:bg-muted"
                        >
                            <input
                                type="checkbox"
                                aria-label={`Severity: ${severity}`}
                                checked={selectedValues.includes(severity)}
                                disabled={disabled}
                                onChange={(event) => setSelected(severity, event.target.checked)}
                            />
                            {severity}
                        </label>
                    ))}
                </div>
            </details>
            {selectedValues.length > 0 ? (
                <div className="flex flex-wrap gap-2" aria-label="Selected Severity">
                    {selectedValues.map((severity) => (
                        <span
                            key={severity}
                            className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2.5 py-1 text-xs font-medium text-blue-900"
                        >
                            {severity}
                            <button
                                type="button"
                                aria-label={`Remove Severity: ${severity}`}
                                disabled={disabled}
                                title={disabled ? GuidedDisabledReason : undefined}
                                className="inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                                onClick={() => setSelected(severity, false)}
                            >
                                <X className="h-3 w-3" aria-hidden="true" />
                            </button>
                        </span>
                    ))}
                </div>
            ) : null}
        </div>
    );
}

function CloudLoggingQueryControls({
    state,
    onChange,
    onSearch,
    errorMessage,
}: CloudLoggingQueryControlsProps): ReactElement {
    const guidedDisabled: boolean = state.mode !== 'guided';
    const rawDisabled: boolean = state.mode !== 'raw';
    const [startInput, setStartInput] = useState<string>(() =>
        formatCloudLoggingTimestampInput(state.guided.start_timestamp)
    );
    const [endInput, setEndInput] = useState<string>(() =>
        formatCloudLoggingTimestampInput(state.guided.end_timestamp)
    );
    const [resourceOptions, setResourceOptions] = useState<ReadonlyArray<CloudLoggingOption>>([]);
    const [logOptions, setLogOptions] = useState<ReadonlyArray<CloudLoggingOption>>([]);
    const guidedPreview: string = compileCloudLoggingFilter(state.guided);
    const parsedStartTimestamp: string | null = parseCloudLoggingTimestampInput(startInput);
    const parsedEndTimestamp: string | null = parseCloudLoggingTimestampInput(endInput);
    const guidedTimestampsValid: boolean =
        parsedStartTimestamp !== null &&
        parsedEndTimestamp !== null &&
        parsedStartTimestamp <= parsedEndTimestamp;
    const searchDisabled: boolean = state.mode === 'guided' && !guidedTimestampsValid;
    const searchDisabledReason: string = 'Enter valid timestamps with Start no later than End.';

    const updateGuided = (
        update: (current: GuidedCloudLoggingDraft) => GuidedCloudLoggingDraft
    ): void => {
        onChange(updateGuidedCloudLoggingDraft(state, update));
    };

    const setTimestamp = (field: 'start_timestamp' | 'end_timestamp', value: string): void => {
        if (field === 'start_timestamp') {
            setStartInput(value);
        } else {
            setEndInput(value);
        }
        const timestamp: string | null = parseCloudLoggingTimestampInput(value);
        if (timestamp !== null) {
            updateGuided((current) => ({ ...current, [field]: timestamp }));
        }
    };

    return (
        <div className="space-y-5">
            <div className="grid gap-4 md:grid-cols-2">
                <div title={guidedDisabled ? GuidedDisabledReason : undefined}>
                    <Label htmlFor="cloud-log-start">Start</Label>
                    <Input
                        id="cloud-log-start"
                        type="datetime-local"
                        value={startInput}
                        disabled={guidedDisabled}
                        onChange={(event) => setTimestamp('start_timestamp', event.target.value)}
                    />
                </div>
                <div title={guidedDisabled ? GuidedDisabledReason : undefined}>
                    <Label htmlFor="cloud-log-end">End</Label>
                    <Input
                        id="cloud-log-end"
                        type="datetime-local"
                        value={endInput}
                        disabled={guidedDisabled}
                        onChange={(event) => setTimestamp('end_timestamp', event.target.value)}
                    />
                </div>
                <div title={guidedDisabled ? GuidedDisabledReason : undefined}>
                    <Label htmlFor="cloud-log-text">Text</Label>
                    <Input
                        id="cloud-log-text"
                        value={state.guided.text ?? ''}
                        disabled={guidedDisabled}
                        placeholder="Search log payloads..."
                        onChange={(event) =>
                            updateGuided((current) => ({
                                ...current,
                                text: event.target.value || undefined,
                            }))
                        }
                    />
                </div>
                <div title={guidedDisabled ? GuidedDisabledReason : undefined}>
                    <Label htmlFor="cloud-log-trace">Trace ID</Label>
                    <Input
                        id="cloud-log-trace"
                        value={state.guided.trace_id ?? ''}
                        disabled={guidedDisabled}
                        placeholder="Exact trace ID"
                        onChange={(event) =>
                            updateGuided((current) => ({
                                ...current,
                                trace_id: event.target.value || undefined,
                            }))
                        }
                    />
                </div>
                <CloudLoggingSeveritySelector
                    selectedValues={state.guided.severities ?? []}
                    disabled={guidedDisabled}
                    onChange={(values) =>
                        updateGuided((current) => ({
                            ...current,
                            severities: values.length > 0 ? [...values] : undefined,
                        }))
                    }
                />
                <CloudLoggingOptionSelector
                    descriptor={ResourceTypeDescriptor}
                    selectedOptions={resourceOptions}
                    disabled={guidedDisabled}
                    onChange={(options) => {
                        setResourceOptions(options);
                        updateGuided((current) => ({
                            ...current,
                            resource_types:
                                options.length > 0
                                    ? options.map((option) => option.value)
                                    : undefined,
                        }));
                    }}
                />
                <CloudLoggingOptionSelector
                    descriptor={LogIdDescriptor}
                    selectedOptions={logOptions}
                    disabled={guidedDisabled}
                    onChange={(options) => {
                        setLogOptions(options);
                        updateGuided((current) => ({
                            ...current,
                            log_names:
                                options.length > 0
                                    ? options.map((option) => option.value)
                                    : undefined,
                        }));
                    }}
                />
            </div>

            <div title={rawDisabled ? RawDisabledReason : undefined}>
                <Label htmlFor="cloud-log-filter">Cloud Logging filter</Label>
                <textarea
                    id="cloud-log-filter"
                    aria-label="Cloud Logging filter"
                    value={rawDisabled ? guidedPreview : state.rawDraft}
                    disabled={rawDisabled}
                    readOnly={rawDisabled}
                    rows={7}
                    className="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 font-mono text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                    onChange={(event) =>
                        onChange(updateRawCloudLoggingDraft(state, event.target.value))
                    }
                />
            </div>

            {errorMessage !== null ? (
                <p role="alert" className="text-sm text-destructive">
                    {errorMessage}
                </p>
            ) : null}
            {searchDisabled ? (
                <p role="alert" className="text-sm text-destructive">
                    {searchDisabledReason}
                </p>
            ) : null}
            <Button
                type="button"
                aria-label="Search cloud logs"
                disabled={searchDisabled}
                title={searchDisabled ? searchDisabledReason : undefined}
                onClick={onSearch}
            >
                Search logs
            </Button>
        </div>
    );
}

function CloudLoggingEntryRow({ item }: { readonly item: CloudLoggingEntry }): ReactElement {
    const keyDetails: ReadonlyArray<string> = [
        item.timestamp,
        item.severity,
        item.resource_type,
        item.log_id,
    ].filter((value: string | undefined): value is string => value !== undefined);

    return (
        <article className="space-y-2 rounded-md border bg-white p-3">
            <div className="text-sm font-semibold text-gray-900">
                {keyDetails.join(' · ') || 'Cloud log entry'}
            </div>
            <dl className="grid gap-x-4 gap-y-1 text-xs text-gray-600 sm:grid-cols-2">
                <div>
                    <dt className="font-medium">Trace</dt>
                    <dd>{item.trace ?? 'Unavailable'}</dd>
                </div>
                <div>
                    <dt className="font-medium">Insert ID</dt>
                    <dd>{item.insert_id ?? 'Unavailable'}</dd>
                </div>
            </dl>
            <div>
                <div className="mb-1 text-sm font-medium text-gray-700">Details</div>
                <pre className="max-h-96 overflow-auto rounded border bg-gray-50 p-3 text-xs">
                    {stringifyLosslessJson(item.details, null, 2)}
                </pre>
            </div>
        </article>
    );
}

export function CloudLoggingContent(): ReactElement {
    const [pageState, setPageState] = useState<CloudLoggingPageState>(
        createInitialCloudLoggingPageState
    );
    const [queryErrorMessage, setQueryErrorMessage] = useState<string | null>(null);
    const queryKeyPrefix = useMemo(
        () => ['cloudLogging', pageState.executedQuery] as const,
        [pageState.executedQuery]
    );
    const fetchPage: InfiniteScrollListFetchPage<CloudLoggingEntry, string> = async (params) => {
        const response = await loggingApi.listCloudLoggingEntriesPage({
            ...pageState.executedQuery,
            limit: params.limit,
            cursor: params.cursor,
        });
        return { items: response.entries, pagination: response.pagination };
    };

    const executeSearch = (): void => {
        const request = buildActiveCloudLoggingQuery(pageState.draft);
        const result = CloudLoggingQueryRequestSchema.safeParse(request);
        if (!result.success) {
            setQueryErrorMessage('Enter a valid query before searching.');
            return;
        }
        setQueryErrorMessage(null);
        setPageState((current) => ({ ...current, executedQuery: result.data }));
    };

    return (
        <InfiniteScrollList<CloudLoggingEntry, string, typeof queryKeyPrefix>
            pageTitle="Cloud Logs"
            cardTitle="Current environment"
            queryPlaceholder=""
            queryKeyPrefix={queryKeyPrefix}
            pageSize={CloudLoggingPageSize}
            fetchPage={fetchPage}
            parseCursorParam={parseCloudLoggingCursor}
            showQueryInput={false}
            controlsPlacement="above-search"
            headerActions={
                <CloudLoggingModeSlider
                    value={pageState.draft.mode}
                    onChange={(mode) =>
                        setPageState((current) => ({
                            ...current,
                            draft: switchCloudLoggingQueryMode(current.draft, mode),
                        }))
                    }
                />
            }
            controls={
                <CloudLoggingQueryControls
                    state={pageState.draft}
                    errorMessage={queryErrorMessage}
                    onChange={(draft) => setPageState((current) => ({ ...current, draft }))}
                    onSearch={executeSearch}
                />
            }
            stateCopy={{
                loadingLabel: 'Loading cloud logs...',
                emptyLabel: 'No cloud log entries found.',
                endOfListLabel: 'End of cloud logs.',
                genericErrorLabel: 'Failed to load cloud logs.',
            }}
            renderItems={({ items }) => (
                <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                        Results are ordered newest first across all resource types in the current
                        environment.
                    </p>
                    {items.map((item, index) => (
                        <CloudLoggingEntryRow
                            key={item.insert_id ?? `${item.timestamp ?? 'entry'}-${index}`}
                            item={item}
                        />
                    ))}
                </div>
            )}
        />
    );
}

export default function CloudLoggingPage(): ReactElement {
    return (
        <ProtectedRoute>
            <MainLayout>
                <SectionErrorBoundary sectionName="Cloud logs">
                    <CloudLoggingContent />
                </SectionErrorBoundary>
            </MainLayout>
        </ProtectedRoute>
    );
}
